import React from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useAppSelector } from "../../redux/typedHooks";
import { UserInterface } from "../../types/user";

const ALLOWED_PATHS = [
  "/member-onboarding-step",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/choose-user-type",
  "/terms-of-service",
  "/privacy-policy",
  "/refund-policy",
  "/acceptable-use-policy",
  "/DMCA-takedown-policy",
  "/compliance-statement",
  "/kyc-aml-compliance-policy",
  "/cookie-policy",
  "/complaints-policy",
  "/content-provider-agreement",
  "/faq",
  "/help",
];

const MemberOnboardingGuard: React.FC = () => {
  const location = useLocation();
  const reduxUser = useAppSelector<UserInterface | null>(
    (state) => state.user.currentUser
  );

  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");

  // Prefer localStorage user to avoid stale in-memory redux state when storage is cleared
  const user = (() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : reduxUser;
    } catch {
      return reduxUser;
    }
  })();

  // If no user is logged in, no token, or user is admin, allow navigation
  if (!user || !token || user.isAdmin) {
    return null;
  }

  // Always allow whitelisted authentication and legal policy routes
  const isAllowed = ALLOWED_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isAllowed) {
    return null;
  }

  const rawUserType = user?.userType || (user as any)?.usertype || (user as any)?.user_type;
  const userTypeTitle = (typeof rawUserType === "string" ? rawUserType : rawUserType?.title)?.toLowerCase();
  const userTypeId = typeof rawUserType === "object" ? ((rawUserType as any)?._id || (rawUserType as any)?.id) : rawUserType;

  const isMember =
    userTypeTitle === "member" ||
    userTypeTitle === "viewer" ||
    userTypeId === "6800a68085ec077c7c266c27" ||
    userTypeTitle === "6800a68085ec077c7c266c27";

  const isMemberComplete =
    (user.emailVerified === true || (user as any).validated === true) &&
    (user.phoneVerified === true || Boolean(user.phoneNumber)) &&
    (user.profileCompletion === true || Boolean((user as any).aboutMe) || user.isOnboarding === true || Boolean((user as any).currentSubscriptionTier));

  console.log("[MemberOnboardingGuard] Path check:", {
    pathname: location.pathname,
    isMember,
    isMemberComplete,
    emailVerified: user?.emailVerified,
    validated: (user as any)?.validated,
    phoneVerified: user?.phoneVerified,
    phoneNumber: user?.phoneNumber,
    profileCompletion: user?.profileCompletion,
    aboutMe: (user as any)?.aboutMe,
    isOnboarding: user?.isOnboarding,
  });

  // If member has not completed email, phone, or profile completion, lock to member onboarding
  if (isMember && !isMemberComplete) {
    console.warn("[MemberOnboardingGuard] Member onboarding incomplete -> Redirecting to /member-onboarding-step");
    return <Redirect to="/member-onboarding-step" />;
  }

  // Fallback check for any user whose email is explicitly unverified
  if (user.emailVerified === false && (user as any).validated !== true) {
    console.warn("[MemberOnboardingGuard] Email unverified -> Redirecting to /member-onboarding-step");
    return <Redirect to="/member-onboarding-step" />;
  }

  return null;
};

export default MemberOnboardingGuard;
